import React, { useEffect, useState } from "react";

import { listFlowRuns, ListFlowRunsParams } from "@/api/runs";
import { Badge } from "@/components/primitives/Badge";
import { Button } from "@/components/primitives/Button";
import { Card } from "@/components/primitives/Card";
import { Run, Step } from "@/types";
import { RequestDetailsDialog } from "./RequestDetailsDialog";

interface RunHistoryProps {
  flowId: string;
  steps: Step[];
  stepId?: string;
  refreshKey?: number;
}

const STATUS_OPTIONS: { value: ListFlowRunsParams["status"]; label: string }[] = [
  { value: "all", label: "All" },
  { value: "success", label: "Success" },
  { value: "failure", label: "Failure" },
  { value: "unknown", label: "Unknown" },
];

const PAGE_SIZE = 50;

export const RunHistory: React.FC<RunHistoryProps> = ({ flowId, steps, stepId, refreshKey }) => {
  const [runs, setRuns] = useState<Run[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<ListFlowRunsParams["status"]>("all");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<Run | null>(null);

  const selectedStep = selectedRun ? steps.find((s) => s.id === selectedRun.step_id) : undefined;

  const getStepForRun = (run: Run) => steps.find((s) => s.id === run.step_id);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    listFlowRuns(flowId, {
      stepId,
      status,
      includeDeletedSteps: includeDeleted,
      limit: PAGE_SIZE,
      offset,
    })
      .then((data) => {
        if (!cancelled) {
          setRuns(data.items);
          setTotal(data.total);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load history");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [flowId, stepId, status, includeDeleted, offset, refreshKey]);

  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + PAGE_SIZE, total);
  const canGoPrev = offset > 0;
  const canGoNext = offset + PAGE_SIZE < total;

  return (
    <>
      <Card className="p-6">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{stepId && steps.find((step) => step.id === stepId)?.step_type === "imported_event" ? "Event History" : "Request History"}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Individual check results for this flow.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="history-status"
                className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
              >
                Status
              </label>
              <select
                id="history-status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as ListFlowRunsParams["status"]);
                  setOffset(0);
                }}
                className="min-w-[140px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-softnu-500 focus:outline-none focus:ring-2 focus:ring-softnu-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={includeDeleted}
                onChange={(e) => {
                  setIncludeDeleted(e.target.checked);
                  setOffset(0);
                }}
                className="h-4 w-4 rounded border-slate-300 text-softnu-600 focus:ring-softnu-500 dark:border-slate-700 dark:bg-slate-800"
              />
              Include deleted steps
            </label>
          </div>
        </div>

        {error && (
          <div
            className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            role="alert"
          >
            {error}
          </div>
        )}

        {isLoading ? (
          <p className="py-10 text-center text-slate-500 dark:text-slate-400">Loading history…</p>
        ) : runs.length === 0 ? (
          <p className="py-10 text-center text-slate-500 dark:text-slate-400">
            No requests match the selected filters.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Index</th>
                    <th className="px-4 py-3">HTTP</th>
                    <th className="px-4 py-3">Step</th>
                    <th className="px-4 py-3">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {runs.map((run) => (
                    <tr
                      key={run.id}
                      className={`transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/30 ${
                        run.status === "failure" || run.metadata ? "cursor-pointer" : ""
                      }`}
                      onClick={() => {
                        if (run.status === "failure" || run.metadata) {
                          setSelectedRun(run);
                        }
                      }}
                      title={
                        run.status === "failure" || run.metadata
                          ? "Click to view request and response details"
                          : undefined
                      }
                    >
                      <td className="px-4 py-3 font-mono text-slate-900 dark:text-white">
                        {new Date(run.started_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={run.status === "success" ? "success" : run.status === "unknown" ? "neutral" : "danger"}>
                          {run.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 font-mono text-slate-900 dark:text-white">
                        {run.event_type ?? getStepForRun(run)?.step_type ?? "—"}
                      </td>
                      <td className="px-4 py-3 font-mono text-slate-900 dark:text-white">{run.source_event_index ?? "—"}</td>
                      <td className="px-4 py-3 font-mono text-slate-900 dark:text-white">
                        {run.http_status ?? "—"}
                      </td>
                      <td className="max-w-[360px] truncate px-4 py-3 font-mono text-slate-900 dark:text-white" title={run.event_name ?? getStepForRun(run)?.name}>
                        {run.event_name ?? getStepForRun(run)?.name ?? run.step_id.slice(0, 8)}
                      </td>
                      <td className="max-w-[320px] overflow-hidden text-ellipsis whitespace-nowrap px-4 py-3 text-slate-600 dark:text-slate-300">
                        {run.status === "failure"
                          ? truncate(run.error_message ?? "No error message", 60)
                          : run.error_message ?? (run.metadata ? "View metadata" : "—")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex items-center justify-between gap-4 border-t border-slate-100 pt-4 dark:border-slate-800">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {start}–{end} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  disabled={!canGoPrev || isLoading}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  disabled={!canGoNext || isLoading}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      <RequestDetailsDialog
        isOpen={selectedRun !== null}
        onClose={() => setSelectedRun(null)}
        run={selectedRun}
        step={selectedStep}
      />
    </>
  );
};

function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return value.slice(0, maxLength) + "…";
}
